// Setup › AI agents on the SettingsPage pattern (BL-189): per agent, each action on its list as
// Automatic, Ask first or Never, never above the action's maximum (agents.policies); its limits on an
// action that runs on its own or carries an amount; then its monthly budget, the company's cap and the
// share at which the owner hears, and the notes the agent keeps for itself. As agents/Settings.tsx draws
// them, with the pattern's rows: a choice saves at once, a typed value when it's left or on Enter (Escape
// puts the saved one back), each saying so beside its control. A setting is per action, so it applies to
// every agent that has it. Only the owner changes these (agents.manage); the manager reads them. The
// receptionist's hours are under Calls. Riverstone keeps Agents › Agent settings (agents/Settings.tsx).
import { useEffect, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react';
import { RpcError } from '@flowology/client';
import { Label, Link, RowList, Segmented, TextField } from '@flowology/ui-web/fl';
import { SettingRow, SettingsPage, SettingsSection, type SettingState } from '@flowology/ui-web/patterns';
import { client } from '../api.ts';
import { money } from '../books/model.ts';
import { useQuery } from '../books/ui.tsx';
import {
  KIND_WORDS,
  POLICIES,
  POLICY_LABEL,
  canChoose,
  limitText,
  parseBudget,
  parseCount,
  parsePercent,
  policyCaption,
  roleLabel,
  type Policy,
  type ToolLimit,
  type ToolPolicy,
} from '../agents/model.ts';
import type { AgentPolicies, AgentsList, MemoryNote } from '../agents/types.ts';
import { PageBanners, usePageCrumb, type NavProps } from '../Nav.tsx';
import { BP_LEDE } from './ledes.ts';
import { settingsPage } from './parts.ts';

const WARN_KEY = 'agents.budget.warnPercent';
const message = (e: unknown) =>
  e instanceof RpcError || e instanceof Error ? e.message : 'Something went wrong.';
const dollars = (cents: number | null) => (cents === null ? '' : String(cents / 100));

/** A save's state beside its control: Saving…, Saved, or what went wrong. */
function useSave() {
  const [state, setState] = useState<SettingState | undefined>(undefined);
  const run = async (call: () => Promise<unknown>) => {
    setState({ status: 'saving' });
    try {
      await call();
      setState({ status: 'saved' });
    } catch (e) {
      setState({ status: 'failed', text: message(e) });
    }
  };
  return { state, run, fail: (text: string) => setState({ status: 'failed', text }) };
}

/**
 * A typed value that saves itself when it's left or on Enter, if it changed and reads; Escape puts the
 * saved one back. `parse` gives null for what doesn't read, and `wrong` says why.
 */
function useTyped<T>({
  saved,
  parse,
  wrong,
  onSave,
}: {
  saved: string;
  parse: (text: string) => T | null;
  wrong: string;
  onSave: (value: T) => Promise<unknown>;
}) {
  const [text, setText] = useState(saved);
  const save = useSave();
  const last = useRef(saved);
  useEffect(() => {
    setText(saved);
    last.current = saved;
  }, [saved]);
  const commit = () => {
    if (text.trim() === last.current.trim()) return;
    const v = parse(text);
    if (v === null) return save.fail(wrong);
    last.current = text;
    void save.run(() => onSave(v));
  };
  return {
    text,
    setText,
    state: save.state,
    onBlur: (e: FocusEvent<HTMLDivElement>) => {
      if (!e.currentTarget.contains(e.relatedTarget as Node | null)) commit();
    },
    onKeyDown: (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') commit();
      if (e.key === 'Escape') setText(last.current);
    },
  };
}

export function AiAgentsPage({ nav }: { nav: NavProps }) {
  const held = nav.permissions ?? [];
  // agents.manage's, which stays with the Owner role
  const isOwner = held.includes('agents.manage');
  const crumb = usePageCrumb(nav);
  const pol = useQuery<AgentPolicies>('agents.policies', {});
  const list = useQuery<AgentsList>('agents.list', {});
  const [key, setKey] = useState('');
  const failed = pol.error ?? list.error;
  const status = pol.data && list.data ? 'ready' : failed ? 'failed' : 'loading';
  const retry = () => void Promise.all([pol.reload(), list.reload()]);
  const agent = pol.data ? (pol.data.agents.find((a) => a.key === key) ?? pol.data.agents[0]) : undefined;
  const summary = agent && list.data?.agents.find((a) => a.key === agent.key);
  return (
    <div className="app">
      {/* the sheet pads the page (blueprint.css .bp-sheet), as PageFrame does; no .page padding on top */}
      <main>
        <PageBanners nav={nav} />
        <SettingsPage
          head={{
            ...(crumb ? { crumb } : {}),
            title: settingsPage('agents').label ?? 'AI agents',
            lede: BP_LEDE.agents,
            // the agents, one page each: what it may do, its budget and its notes
            ...(pol.data && pol.data.agents.length > 1 && agent
              ? {
                  tabs: {
                    label: 'Agent',
                    items: pol.data.agents.map((a) => ({
                      label: a.name,
                      isCurrent: a.key === agent.key,
                      onPress: () => setKey(a.key),
                    })),
                  },
                }
              : {}),
          }}
        >
          <SettingsSection
            title={agent?.name ?? 'What each agent may do'}
            line="It sees only the actions below: its role’s, narrowed to its own list."
            {...(agent ? { action: <Label>Acts as {roleLabel(agent.role)}</Label> } : {})}
            note={
              isOwner
                ? 'A setting is per action, so it applies to every agent that has it.'
                : 'Read-only: the owner changes these. A setting is per action, so it applies to every agent that has it.'
            }
            {...(agent?.key === 'receptionist'
              ? {
                  foot: (
                    <>
                      When the receptionist answers moved to{' '}
                      <Link href="#/settings/calls">Setup › Calls</Link>.
                    </>
                  ),
                }
              : {})}
            status={status}
            failedText={failed ?? undefined}
            onRetry={retry}
          >
            {agent?.tools.map((t) => (
              <PolicyRow key={t.action} t={t} isOwner={isOwner} onSaved={() => pol.reload()} />
            ))}
          </SettingsSection>
          <SettingsSection
            title="Budget"
            foot="At the cap the agent stops until next month."
            status={status}
            failedText={failed ?? undefined}
            onRetry={retry}
          >
            {agent && summary && list.data && pol.data ? (
              <>
                <BudgetRow
                  name={agent.name}
                  agentKey={agent.key}
                  budgetCents={summary.budgetCents}
                  spentCents={summary.spentCents}
                  isOwner={isOwner}
                  onSaved={() => list.reload()}
                />
                <BudgetRow
                  name="All agents together"
                  agentKey="*"
                  budgetCents={list.data.company.budgetCents}
                  spentCents={list.data.company.spentCents}
                  isOwner={isOwner}
                  onSaved={() => list.reload()}
                />
                <WarnRow percent={pol.data.warnPercent} isOwner={isOwner} onSaved={() => pol.reload()} />
              </>
            ) : null}
          </SettingsSection>
          {agent ? <Notes key={agent.key} agentKey={agent.key} /> : null}
        </SettingsPage>
      </main>
    </div>
  );
}

/** One action: Automatic, Ask first or Never, never above its maximum; its limits under it. */
function PolicyRow({
  t,
  isOwner,
  onSaved,
}: {
  t: ToolPolicy;
  isOwner: boolean;
  onSaved: () => Promise<void>;
}) {
  const save = useSave();
  const key = `agents.policy.${t.action}`;
  const pick = (id: string) => {
    const p = id as Policy;
    if (!isOwner || p === t.current || !canChoose(p, t.max, t.blocked)) return;
    // the action's default is no setting at all
    void save.run(async () => {
      await (p === t.default
        ? client.call('settings.clear', { key })
        : client.call('settings.set', { key, value: p }));
      await onSaved();
    });
  };
  // Only what is worth reading: a plain "Default: X." on a row still at its default says nothing.
  const caption = policyCaption(t);
  const said = !(caption === `Default: ${POLICY_LABEL[t.default]}.` && t.current === t.default);
  // A count bites only on what runs on its own; a ceiling also labels a request that asks anyway.
  const limits =
    t.limit && (t.current === 'auto' || (t.current === 'ask_first' && t.limit.hasAmount)) ? t.limit : null;
  const help = `${t.action} · ${KIND_WORDS[t.kind] ?? t.kind}`;
  return (
    <>
      <SettingRow
        name={t.title}
        help={
          said ? (
            <>
              {help}
              <br />
              {caption}
            </>
          ) : (
            help
          )
        }
        control={
          <Segmented
            label={`Policy for ${t.title}`}
            items={POLICIES.map((p) => ({
              id: p,
              label: POLICY_LABEL[p],
              // the current choice stays readable, never greyed
              isDisabled: p !== t.current && (!isOwner || !canChoose(p, t.max, t.blocked)),
            }))}
            selectedKey={t.current}
            onSelectionChange={pick}
          />
        }
        {...(save.state ? { state: save.state } : {})}
      />
      {limits ? <Limits t={t} limit={limits} isOwner={isOwner} onSaved={onSaved} /> : null}
    </>
  );
}

/**
 * An agent's limits on an action (M10, agents.limit.<action>): a ceiling where it carries an amount, and
 * how many a day where it runs on its own. Over either, what would run on its own asks first, saying why.
 * Blank is no limit; Default goes back to the action's.
 */
function Limits({
  t,
  limit,
  isOwner,
  onSaved,
}: {
  t: ToolPolicy;
  limit: ToolLimit;
  isOwner: boolean;
  onSaved: () => Promise<void>;
}) {
  const key = `agents.limit.${t.action}`;
  const counts = t.current === 'auto';
  // both saved together, as one setting: the other as it's saved
  const set = async (change: { maxCents?: number | null; perDay?: number | null }) => {
    const v = { maxCents: limit.maxCents, perDay: limit.perDay, ...change };
    const value = {
      ...(limit.hasAmount ? { maxCents: v.maxCents } : {}),
      ...(counts ? { perDay: v.perDay } : {}),
    };
    await client.call('settings.set', { key, value });
    await onSaved();
  };
  const max = useTyped({
    saved: dollars(limit.maxCents),
    parse: parseBudget,
    wrong: 'Type an amount in dollars, like 120.',
    onSave: (c) => set({ maxCents: c === 'none' ? null : c }),
  });
  const day = useTyped({
    saved: limit.perDay === null ? '' : String(limit.perDay),
    parse: parseCount,
    wrong: 'A whole number from 1 to 10,000.',
    onSave: (n) => set({ perDay: n === 'none' ? null : n }),
  });
  const words = `${limitText(limit, money)}. ${
    counts
      ? 'Over a limit, it asks first and says why.'
      : 'It asks first anyway; over a limit, its request says so.'
  }${
    limit.set
      ? ` The default is ${limitText(
          { ...limit, maxCents: limit.defaultMaxCents, perDay: limit.defaultPerDay },
          money,
        ).toLowerCase()}.`
      : ''
  }`;
  // the limits' words and where they come from, under the last of them
  const last = {
    help: words,
    ...(limit.set
      ? {
          source: {
            from: 'changed' as const,
            ...(isOwner
              ? {
                  onBack: () =>
                    void client
                      .call('settings.clear', { key })
                      .then(onSaved)
                      .catch(() => undefined),
                }
              : {}),
          },
        }
      : {}),
  };
  return (
    <>
      {limit.hasAmount ? (
        <SettingRow
          name="Up to ($)"
          {...(counts ? {} : last)}
          control={
            <TextField
              label={`${t.title}: up to ($)`}
              hideLabel
              width="number"
              inputMode="decimal"
              value={max.text}
              placeholder="No ceiling"
              isDisabled={!isOwner}
              onChange={max.setText}
            />
          }
          {...(max.state ? { state: max.state } : {})}
          isSub
          onControlBlur={max.onBlur}
          onControlKeyDown={max.onKeyDown}
        />
      ) : null}
      {counts ? (
        <SettingRow
          name="Times a day"
          {...last}
          control={
            <TextField
              label={`${t.title}: times a day`}
              hideLabel
              width="number"
              inputMode="numeric"
              value={day.text}
              placeholder="Any"
              isDisabled={!isOwner}
              onChange={day.setText}
            />
          }
          {...(day.state ? { state: day.state } : {})}
          isSub
          onControlBlur={day.onBlur}
          onControlKeyDown={day.onKeyDown}
        />
      ) : null}
    </>
  );
}

/** An agent's monthly budget, or the company's cap ('*'): blank is no cap. */
function BudgetRow({
  name,
  agentKey,
  budgetCents,
  spentCents,
  isOwner,
  onSaved,
}: {
  name: string;
  agentKey: string;
  budgetCents: number | null;
  spentCents: number;
  isOwner: boolean;
  onSaved: () => Promise<void>;
}) {
  const typed = useTyped({
    saved: dollars(budgetCents),
    parse: parseBudget,
    wrong: 'Type an amount in dollars, like 120.',
    onSave: async (c) => {
      await client.call('agents.setBudget', { agentKey, monthlyBudgetCents: c === 'none' ? null : c });
      await onSaved();
    },
  });
  return (
    <SettingRow
      name={name}
      help={`Spent this month: ${money(spentCents)}`}
      control={
        <TextField
          label={`${name} ($ a month)`}
          hideLabel
          width="number"
          inputMode="decimal"
          unit="$ a month"
          value={typed.text}
          placeholder="No cap"
          isDisabled={!isOwner}
          onChange={typed.setText}
        />
      }
      {...(typed.state ? { state: typed.state } : {})}
      onControlBlur={typed.onBlur}
      onControlKeyDown={typed.onKeyDown}
    />
  );
}

/** The share of a budget at which the owner hears, once a month per agent (M10). */
function WarnRow({
  percent,
  isOwner,
  onSaved,
}: {
  percent: number;
  isOwner: boolean;
  onSaved: () => Promise<void>;
}) {
  const typed = useTyped({
    saved: String(percent),
    parse: parsePercent,
    wrong: 'A whole number from 1 to 99.',
    onSave: async (p) => {
      await client.call('settings.set', { key: WARN_KEY, value: p });
      await onSaved();
    },
  });
  return (
    <SettingRow
      name="Tell me at"
      help="Once a month for each agent, and for all of them against the cap."
      control={
        <TextField
          label="Tell me at (% of a budget)"
          hideLabel
          width="number"
          inputMode="numeric"
          unit="% of a budget"
          value={typed.text}
          isDisabled={!isOwner}
          onChange={typed.setText}
        />
      }
      {...(typed.state ? { state: typed.state } : {})}
      onControlBlur={typed.onBlur}
      onControlKeyDown={typed.onKeyDown}
    />
  );
}

/** What the agent wrote down for itself; anyone who reads them can take one out (it stays in the history). */
function Notes({ agentKey }: { agentKey: string }) {
  const notes = useQuery<{ notes: MemoryNote[] }>('memory.list', { agentKey });
  const [busy, setBusy] = useState(false);
  const [said, setSaid] = useState<string | null>(null);
  const remove = async (id: string) => {
    setBusy(true);
    try {
      await client.call('memory.remove', { id });
      setSaid('The note is out of its memory.');
      await notes.reload();
    } catch (e) {
      setSaid(message(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <SettingsSection
      title="Its notes"
      count={notes.data?.notes.length ?? 0}
      line="What the agent wrote down for itself. Only agents write notes; you can take one out (it stays in the history)."
      {...(said ? { note: said } : {})}
      status={notes.data ? 'ready' : notes.error ? 'failed' : 'loading'}
      failedText={notes.error ?? undefined}
      onRetry={() => void notes.reload()}
    >
      <RowList
        label="Its notes"
        empty="No notes."
        rows={(notes.data?.notes ?? []).map((n) => ({
          id: n.id,
          name: n.body,
          lines: [{ text: new Date(n.writtenAt).toLocaleDateString('en-CA', { dateStyle: 'medium' }) }],
          actions: [
            {
              id: 'remove',
              text: 'Remove',
              label: `Remove the note: ${n.body}`,
              isDisabled: busy,
              onPress: () => void remove(n.id),
            },
          ],
        }))}
      />
    </SettingsSection>
  );
}
